// One global flag so every motion-using block can pause / shortcut for users
// who set OS-level prefers-reduced-motion.
const PREFERS_REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// AOS scroll animations — only init if the library was loaded (opt-in via $useAos in config.php)
window.addEventListener("load", () => {
  if (typeof AOS !== "undefined") {
    AOS.init({ disable: PREFERS_REDUCED_MOTION ? true : "mobile" });
  }
});

// Reveal-on-scroll — fade + slide-up for [data-reveal] elements. CSS handles
// the visual states (responsive.css); JS just toggles .is-revealed when the
// element enters the viewport. Cheap, library-free, IntersectionObserver-only.
// Skipped entirely when prefers-reduced-motion is set — CSS rule above already
// jumps revealed elements to their final state, so the observer is unnecessary.
(function () {
  if (PREFERS_REDUCED_MOTION) return;
  const els = document.querySelectorAll("[data-reveal]");
  if (!els.length) return;
  if (!("IntersectionObserver" in window)) {
    // Old browsers — skip the animation, show everything immediately.
    els.forEach((el) => el.classList.add("is-revealed"));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        // Stagger optional via data-reveal-delay="120" (ms).
        const delay = parseInt(entry.target.dataset.revealDelay || "0", 10);
        if (delay > 0) {
          setTimeout(() => entry.target.classList.add("is-revealed"), delay);
        } else {
          entry.target.classList.add("is-revealed");
        }
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
  );
  els.forEach((el) => io.observe(el));
})();

// Testimonials swiper — only init if Swiper is available (opt-in via $useSwiper)
window.addEventListener("load", () => {
  if (typeof Swiper === "undefined") return;
  const el = document.querySelector(".testimonials-swiper");
  if (!el) return;
  const swiper = new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 24,
    loop: true,
    // Skip autoplay entirely when the user has asked for reduced motion.
    autoplay: PREFERS_REDUCED_MOTION
      ? false
      : { delay: 6000, disableOnInteraction: false },
    a11y: {
      enabled: true,
      prevSlideMessage: "Previous testimonial",
      nextSlideMessage: "Next testimonial",
    },
    pagination: {
      el: ".testimonials-pagination",
      clickable: true,
    },
    // Recalculate slide widths when the container or any ancestor resizes
    // (e.g. fonts loading, images loading, viewport resize). Without this,
    // initial layout can race and slides end up sized to 0 or full-viewport.
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
  });
  if (PREFERS_REDUCED_MOTION && swiper.autoplay) swiper.autoplay.stop();
});

// Culture testimonials swiper — peek-next, custom arrows on desktop, autoplay
// on mobile. `overflow-visible!` on the .culture-swiper container lets the
// next slide bleed past; the section's overflow-hidden contains it. Active
// slide is full opacity, non-active slides drop to ~0.35 (CSS fade).
window.addEventListener("load", () => {
  if (typeof Swiper === "undefined") return;
  const el = document.querySelector(".culture-swiper");
  if (!el) return;
  const culture = new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 12,
    loop: true,
    // Skip autoplay entirely when reduced-motion is set — overrides the
    // mobile-only autoplay logic below.
    autoplay: PREFERS_REDUCED_MOTION
      ? false
      : { delay: 5000, disableOnInteraction: false },
    a11y: {
      enabled: true,
      prevSlideMessage: "Previous culture testimonial",
      nextSlideMessage: "Next culture testimonial",
    },
    breakpoints: {
      // md+: small peek (1.1 = each slide takes ~91% of swiper width, next
      // slide peeks ~9%). spaceBetween between slides locked at 12px per
      // Figma spec.
      768: { slidesPerView: 1.1, spaceBetween: 12 },
      1024: { slidesPerView: 1.1, spaceBetween: 12 },
      1280: { slidesPerView: 1.1, spaceBetween: 12 },
    },
    pagination: {
      el: ".culture-pagination",
      clickable: true,
    },
    navigation: {
      prevEl: ".culture-nav-prev",
      nextEl: ".culture-nav-next",
    },
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
  });

  // Autoplay on mobile only (< 768px). On desktop the nav arrows are visible,
  // so no autoplay — user drives. Listens to viewport changes so rotating the
  // device or resizing the window flips the behaviour live. Reduced-motion
  // users override everything: autoplay stays stopped no matter the viewport.
  const desktopMq = window.matchMedia("(min-width: 768px)");
  const syncAutoplay = () => {
    if (!culture.autoplay) return;
    if (PREFERS_REDUCED_MOTION || desktopMq.matches) culture.autoplay.stop();
    else culture.autoplay.start();
  };
  desktopMq.addEventListener("change", syncAutoplay);
  syncAutoplay();
});

// Stats bar — counter-up animation on scroll. Each [data-stat-target] element
// counts from 0 to its target when it enters the viewport. Honors prefers-reduced-motion.
(function () {
  const counters = document.querySelectorAll("[data-stat-target]");
  if (!counters.length) return;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  if (reducedMotion) {
    counters.forEach((el) => {
      el.textContent = el.dataset.statTarget + (el.dataset.statSuffix || "");
    });
    return;
  }

  const animate = (el) => {
    const target = parseInt(el.dataset.statTarget, 10);
    const suffix = el.dataset.statSuffix || "";
    const duration = 1800;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      el.textContent = Math.floor(eased * target) + suffix;
      if (progress < 1) requestAnimationFrame(tick);
      else el.textContent = target + suffix;
    };
    requestAnimationFrame(tick);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animate(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.4 },
  );

  counters.forEach((el) => observer.observe(el));
})();

// Consultation form — client-side validation. Runs on submit; if anything is
// invalid, blocks the POST, paints invalid inputs red, and lists each error in
// the inline summary box near the submit button. Server still re-validates in
// partials/form.php (defense in depth — never trust the client).
(function () {
  const form = document.querySelector(".consultation-form");
  if (!form) return;

  const errorBox = form.querySelector("[data-form-errors]");
  const errorList = form.querySelector("[data-form-errors-list]");

  // Field name → human label, validator returns null on pass or string on fail.
  const validators = {
    name: { label: "Name", test: required },
    email: { label: "Email", test: emailField },
    contact_number: { label: "Contact Number", test: phoneField },
    company_name: { label: "Company Name", test: required },
  };

  function required(v) {
    return v.trim() ? null : "is required";
  }
  function emailField(v) {
    if (!v.trim()) return "is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()))
      return "doesn’t look like a valid email";
    return null;
  }
  function phoneField(v) {
    const digits = v.replace(/\D+/g, "");
    if (!digits) return "is required";
    if (!/^0[2|3|4|7|8]\d{8}$/.test(digits))
      return "must be a 10-digit Australian number starting with 0";
    return null;
  }

  function setInvalid(input) {
    input.classList.add("form-input-invalid");
    input.setAttribute("aria-invalid", "true");
  }
  function clearInvalid(input) {
    input.classList.remove("form-input-invalid");
    input.removeAttribute("aria-invalid");
  }

  // As the user types/changes, clear that field's red state — feels nicer than
  // staying red until the next submit.
  form.querySelectorAll("input, select, textarea").forEach((el) => {
    el.addEventListener("input", () => clearInvalid(el));
    el.addEventListener("change", () => clearInvalid(el));
  });

  // Live input filtering — phone field accepts digits only. Anything pasted or
  // typed that isn't 0-9 is stripped on the spot, with the caret restored.
  const phoneInput = form.querySelector('[name="contact_number"]');
  if (phoneInput) {
    phoneInput.addEventListener("input", (e) => {
      const start = e.target.selectionStart;
      const before = e.target.value;
      const cleaned = before.replace(/\D+/g, "");
      if (cleaned !== before) {
        e.target.value = cleaned;
        // Restore caret near where the user was (minus stripped chars before it).
        const removedBefore = before.slice(0, start).replace(/\D+/g, "").length;
        e.target.setSelectionRange(removedBefore, removedBefore);
      }
    });
  }

  // Email field — `type="email"` already shows the right mobile keyboard and
  // hints, JS format validation runs on submit. No live filter (people legitimately
  // need + - . _ in emails), but we trim leading/trailing whitespace.
  const emailInput = form.querySelector('[name="email"]');
  if (emailInput) {
    emailInput.addEventListener("blur", (e) => {
      e.target.value = e.target.value.trim();
    });
  }

  form.addEventListener("submit", (e) => {
    const errors = [];

    Object.entries(validators).forEach(([name, { label, test }]) => {
      const input = form.querySelector(`[name="${name}"]`);
      if (!input) return;
      const msg = test(input.value);
      if (msg) {
        setInvalid(input);
        errors.push(`${label} ${msg}.`);
      } else {
        clearInvalid(input);
      }
    });

    if (errors.length) {
      e.preventDefault();
      if (errorList) {
        errorList.innerHTML = errors
          .map((m) => {
            const li = document.createElement("li");
            li.textContent = m;
            return li.outerHTML;
          })
          .join("");
      }
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      // Focus the first invalid field for keyboard users.
      const firstInvalid = form.querySelector(".form-input-invalid");
      if (firstInvalid) firstInvalid.focus({ preventScroll: true });
      return;
    }

    // All good — hide the error box and let the form submit normally.
    if (errorBox) errorBox.hidden = true;
  });
})();

// Talent profiles accordion — clicking a card sets it active, others collapse.
// Horizontal width transition on desktop, vertical height transition on mobile
// (CSS handles both via [data-active] selectors).
//
// Also wires click-and-drag scroll on desktop. The row uses overflow-x-auto
// for horizontal scroll, but desktop browsers don't pan that with mouse drag —
// only with the scrollbar or trackpad. Touch devices already pan natively, so
// pointerdown from a touch input is ignored. Drag distance > threshold also
// suppresses the click that follows, so dragging across a card doesn't toggle it.
(function () {
  const row = document.querySelector("[data-talent-row]");
  if (!row) return;
  const scroll = row.closest(".talent-scroll");
  const cards = row.querySelectorAll("[data-talent-card]");

  // Drag-to-scroll state
  let isDown = false;
  let dragged = false;
  let startX = 0;
  let startScrollLeft = 0;
  const DRAG_THRESHOLD = 6;

  if (scroll) {
    // Belt-and-braces: stop the browser firing its native drag-and-drop ghost
    // on images/links inside the row. CSS already does this on modern
    // browsers via `-webkit-user-drag: none`; this catches the rest.
    scroll.addEventListener("dragstart", (e) => e.preventDefault());

    scroll.addEventListener("pointerdown", (e) => {
      // Touch already pans the overflow natively; only handle mouse/pen.
      if (e.pointerType === "touch") return;
      isDown = true;
      dragged = false;
      startX = e.clientX;
      startScrollLeft = scroll.scrollLeft;
      // Don't add `.is-dragging` here — it would apply pointer-events:none to
      // all card descendants and swallow the click that follows a clean
      // (non-drag) press. Only switch to "is-dragging" once the threshold is
      // actually exceeded in pointermove below.
    });

    scroll.addEventListener("pointermove", (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!dragged && Math.abs(dx) > DRAG_THRESHOLD) {
        dragged = true;
        scroll.classList.add("is-dragging");
      }
      if (dragged) {
        e.preventDefault();
        scroll.scrollLeft = startScrollLeft - dx;
      }
    });

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      scroll.classList.remove("is-dragging");
    };
    scroll.addEventListener("pointerup", endDrag);
    scroll.addEventListener("pointercancel", endDrag);
    scroll.addEventListener("pointerleave", endDrag);

    // Capture-phase click suppression — runs before the card's own listener.
    // Only swallows clicks that came from a real drag (dragged === true).
    // Clean clicks pass through unchanged.
    scroll.addEventListener(
      "click",
      (e) => {
        if (dragged) {
          e.stopPropagation();
          e.preventDefault();
          dragged = false;
        }
      },
      true,
    );
  }

  const activate = (target) => {
    cards.forEach((c) => {
      const isTarget = c === target;
      c.dataset.active = isTarget ? "true" : "false";
      c.setAttribute("aria-expanded", isTarget ? "true" : "false");
    });
  };

  // Close button inside an active card — collapses ALL cards (no auto-fallback
   // to the first one; the user explicitly chose to close, so respect that).
   // stopPropagation prevents the click from also reaching the card's handler.
  row.querySelectorAll("[data-talent-close]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      cards.forEach((c) => {
        c.dataset.active = "false";
        c.setAttribute("aria-expanded", "false");
      });
    });
  });

  cards.forEach((card) => {
    // Click only activates when the user actually clicked on the IMAGE column
    // (the portrait + name overlay). The content panel is for reading and
    // the surrounding gap is reserved for drag-scrolling — clicking either
    // does nothing. This matches the user's intent: image col toggles,
    // everywhere else drags.
    card.addEventListener("click", (e) => {
      // The close-button has its own handler above and stops propagation,
      // so we never reach this branch from a close click. But guard anyway.
      if (e.target.closest("[data-talent-close]")) return;
      if (e.target.closest(".talent-image-col")) activate(card);
    });
    card.addEventListener("keydown", (e) => {
      // Enter or space activates (matches [role=button] semantics).
      // Keyboard users always toggle — they can't drag — so no image-col guard.
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        activate(card);
      }
    });
  });
})();

// Video modal — open on [data-video-trigger] click, close on backdrop or [data-video-close].
// Loads the <source> on open (preload=none keeps the file off the wire until then)
// and pauses + clears src on close so the browser stops downloading.
(function () {
  const modal = document.querySelector("[data-video-modal]");
  if (!modal) return;
  // Mark as a dialog for AT — modal context, not just a hidden region.
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Video player");
  const player = modal.querySelector("[data-video-player]");
  const source = modal.querySelector("[data-video-source]");
  const triggers = document.querySelectorAll("[data-video-trigger]");
  const closers = modal.querySelectorAll("[data-video-close]");

  // Track the element that opened the modal so we can restore focus on close.
  let lastFocused = null;

  // Returns all interactive elements inside the modal — Tab cycles between
  // these, never escaping into the underlying page (focus trap).
  const focusable = () =>
    modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), video[controls]',
    );

  const open = (src, type, trigger) => {
    lastFocused = trigger || document.activeElement;
    source.setAttribute("src", src);
    if (type) source.setAttribute("type", type);
    player.load();
    modal.removeAttribute("hidden");
    document.body.classList.add("modal-open");
    // Move focus into the dialog so subsequent Tab moves stay inside it.
    const closeBtn = modal.querySelector("[data-video-close]");
    if (closeBtn) closeBtn.focus({ preventScroll: true });
    player.play().catch(() => {
      /* autoplay may be blocked — user can press play */
    });
  };
  const close = () => {
    player.pause();
    modal.setAttribute("hidden", "");
    source.setAttribute("src", "");
    player.load();
    document.body.classList.remove("modal-open");
    // Restore focus to the trigger (WCAG 2.4.3 Focus Order).
    if (lastFocused && typeof lastFocused.focus === "function") {
      lastFocused.focus({ preventScroll: true });
    }
    lastFocused = null;
  };

  triggers.forEach((t) => {
    t.addEventListener("click", () => {
      const src = t.getAttribute("data-video-src");
      const type = t.getAttribute("data-video-type") || "video/mp4";
      if (src) open(src, type, t);
    });
  });
  closers.forEach((c) => c.addEventListener("click", close));
  document.addEventListener("keydown", (e) => {
    if (modal.hasAttribute("hidden")) return;
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "Tab") {
      // Focus trap — cycle Tab between first/last focusable inside dialog.
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
})();

document.querySelectorAll('a[href="#"]').forEach((link) => {
  link.addEventListener("click", (e) => e.preventDefault());
});

// Smooth scroll for anchor links without updating the URL hash
// Select all anchor links that start with # but are not just '#'
document.querySelectorAll('a[href^="#"]:not([href="#"])').forEach((link) => {
  link.addEventListener("click", function (e) {
    const targetId = this.getAttribute("href").slice(1);
    const target = document.getElementById(targetId);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth" });
      // Do not update the URL hash
      // Optionally, you can manage focus for accessibility:
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    }
  });
});
